import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Spinner from "./Spinner.jsx";
import QuagganEmoji from "./QuagganEmoji.jsx";

export default function DeleteConfirmationWindow({
  visible,
  title,
  processing,
  error,
  succeeded,
  onCancel,
  onConfirm,
  onSuccessClose,
}) {
  const [confirmation, setConfirmation] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!succeeded || !onSuccessClose) return;
    const timer = setTimeout(onSuccessClose, 4000);
    return () => clearTimeout(timer);
  }, [succeeded, onSuccessClose]);

  useEffect(() => {
    if (!visible) {
      setConfirmation("");
      return;
    }

    setConfirmation("");

    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [visible]);

  if (!visible) return null;

  const canDelete = confirmation.trim().toLowerCase() === "delete" && !processing;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canDelete) return;
    onConfirm();
  };

  return createPortal(
    <div>
      <div
        onClick={succeeded ? onSuccessClose : (processing ? undefined : onCancel)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(2, 10, 30, 0.82)",
          backdropFilter: "blur(6px)",
          zIndex: 1400,
          cursor: succeeded ? "pointer" : "default",
        }}
      />

      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1401,
          pointerEvents: "none",
        }}
      >
        <form
          onSubmit={handleSubmit}
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(480px, calc(100vw - 32px))",
            borderRadius: 20,
            background: "linear-gradient(160deg, rgba(10, 35, 70, 0.98) 0%, rgba(3, 16, 42, 0.98) 100%)",
            border: "1px solid rgba(80, 180, 255, 0.38)",
            boxShadow: "0 0 40px rgba(0, 150, 255, 0.2), 0 8px 28px rgba(0, 0, 0, 0.72)",
            padding: 24,
            color: "#e0f7fa",
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
        >
          {succeeded ? (
            <>
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, color: "#40e8ff" }}>
                Deleted!
              </div>
              <p style={{ margin: "0 0 10px", color: "rgba(214, 232, 255, 0.86)", lineHeight: 1.5 }}>
                Game successfully deleted.
              </p>
              {title ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "rgba(8, 24, 50, 0.72)",
                    border: "1px solid rgba(144, 224, 239, 0.18)",
                    color: "#b9f1ff",
                    fontWeight: 700,
                  }}
                >
                  {title}
                </div>
              ) : null}
              <QuagganEmoji type="success" />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button type="button" className="names-btn primary" onClick={onSuccessClose}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, color: "#40e8ff" }}>
            Delete Game
          </div>
          <p style={{ margin: "0 0 10px", color: "rgba(214, 232, 255, 0.86)", lineHeight: 1.5 }}>
            Are you sure? Type "delete" and press delete.
          </p>
          {title ? (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                borderRadius: 12,
                background: "rgba(8, 24, 50, 0.72)",
                border: "1px solid rgba(144, 224, 239, 0.18)",
                color: "#b9f1ff",
                fontWeight: 700,
              }}
            >
              {title}
            </div>
          ) : null}

          {processing ? (
            <div style={{ padding: "18px 0 8px" }}>
              <Spinner size={80} />
              <p className="login-help-text" style={{ textAlign: "center", marginTop: -4 }}>
                Deleting game...
              </p>
            </div>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                className="login-input"
                placeholder="Type delete"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                style={{ marginBottom: 14 }}
              />

              {error ? (
                <>
                  <div
                    className="register-error"
                    style={{
                      whiteSpace: "pre-wrap",
                      marginBottom: 14,
                      padding: 10,
                      borderRadius: 10,
                      background: "rgba(120, 20, 20, 0.38)",
                    }}
                  >
                    {error}
                  </div>
                  <QuagganEmoji type="failure" />
                </>
              ) : null}
            </>
          )}

          {!succeeded && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="names-btn secondary" onClick={onCancel} disabled={processing}>
                Cancel
              </button>
              <button
                type="submit"
                className="names-btn primary"
                disabled={!canDelete}
                style={{
                  borderColor: "rgba(255, 120, 120, 0.78)",
                  background: "radial-gradient(circle at 20% 0%, rgba(170, 30, 60, 0.95), rgba(70, 5, 20, 0.95))",
                }}
              >
                Delete
              </button>
            </div>
          )}
            </>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
}